import type { MemberOut, TeamOut } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Button, Icon, Input, Tooltip } from "@sagot/ui";
import { useState } from "react";
import { useOrderHandler } from "../features/workspace/billing/checkout";
import { useMembers, usePermissionCatalogue, useRoles, useTeams } from "../roles/use-roles";
import { usePermissions } from "../session/permissions";
import { useWorkspaceContext } from "../session/workspace-context";
import { InviteDialog } from "./invite-dialog";
import { MemberRolesDialog } from "./member-roles-dialog";
import { MembersCard } from "./members-card";
import { RemoveMemberDialog } from "./remove-member-dialog";
import { RolesOverviewCard } from "./roles-overview-card";
import { AddSeatsDialog, SeatsCard } from "./seats-card";
import { TeamDialog } from "./team-dialog";
import { TeamsCard } from "./teams-card";
import { useAllowedDomains, useSeats } from "./use-team";

type TeamEditor = { team: TeamOut | null } | null;

function InviteButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const allowed = permissions.can("team.invite") && !permissions.readOnly;
  const button = (
    <Button disabled={!allowed} onClick={onClick}>
      <Icon name="user-plus" />
      {t("team.inviteMembers")}
    </Button>
  );
  if (allowed) return button;
  return (
    <Tooltip content={permissions.readOnly ? t("console.readOnlyTooltip") : t("team.invite.noPermission")}>
      <span className="inline-flex">{button}</span>
    </Tooltip>
  );
}

/** `/w/:ws/team` (tenant/team.html): seats, members + invites, role overview, teams and routing. */
export function TeamPage() {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const members = useMembers();
  const roles = useRoles();
  const teams = useTeams();
  const catalogue = usePermissionCatalogue();
  const permissions = usePermissions();
  const seats = useSeats();
  const allowedDomains = useAllowedDomains();
  const orders = useOrderHandler();
  const [query, setQuery] = useState("");
  const [inviting, setInviting] = useState(false);
  const [addingSeats, setAddingSeats] = useState(false);
  const [teamEditor, setTeamEditor] = useState<TeamEditor>(null);
  const [editing, setEditing] = useState<MemberOut | null>(null);
  const [removing, setRemoving] = useState<MemberOut | null>(null);
  const total = (catalogue.data ?? []).reduce((n, g) => n + g.permissions.length, 0);
  const canManageTeams = permissions.can("team.manage") && !permissions.readOnly;

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">{t("team.title")}</h1>
          <p className="mt-1 text-fg-muted">{t("team.subtitle", { workspace: summary.name })}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input type="search" className="h-9 w-56" placeholder={t("team.search")} aria-label={t("team.search")} value={query} onChange={(e) => { setQuery(e.target.value); }} />
          <InviteButton onClick={() => { setInviting(true); }} />
        </div>
      </div>
      <SeatsCard info={seats} onAddSeats={() => { setAddingSeats(true); }} />
      <MembersCard members={members} roles={roles.data} teams={teams.data ?? []} query={query} onEditRoles={setEditing} onRemove={setRemoving} />
      <section className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <RolesOverviewCard roles={roles.data} isError={roles.isError} onRetry={() => void roles.refetch()} />
        <TeamsCard
          teams={teams.data}
          members={members.data ?? []}
          isError={teams.isError}
          onRetry={() => void teams.refetch()}
          canManage={canManageTeams}
          onCreate={() => { setTeamEditor({ team: null }); }}
          onEdit={(team) => { setTeamEditor({ team }); }}
        />
      </section>
      {inviting && roles.data ? <InviteDialog open onOpenChange={setInviting} roles={roles.data} teams={teams.data ?? []} seats={seats.state} allowedDomains={allowedDomains} /> : null}
      <AddSeatsDialog open={addingSeats} onOpenChange={setAddingSeats} onOrder={(order, quantity) => { orders.handle(order, t("team.seats.seatsAdded", { count: quantity })); }} />
      {teamEditor ? <TeamDialog key={teamEditor.team?.id ?? "new"} team={teamEditor.team} members={members.data ?? []} open onOpenChange={(open) => { if (!open) setTeamEditor(null); }} /> : null}
      {editing && roles.data ? (
        <MemberRolesDialog member={editing} roles={roles.data} teams={teams.data ?? []} totalPermissions={total} open onOpenChange={(open) => { if (!open) setEditing(null); }} />
      ) : null}
      {removing ? <RemoveMemberDialog member={removing} open onOpenChange={(open) => { if (!open) setRemoving(null); }} /> : null}
      {orders.dialog}
    </div>
  );
}

